import React, { useState, useEffect, useContext } from 'react';
import { Box, Typography, Button, Chip, CircularProgress, Alert, Paper } from '@mui/material';
import { NavigateBefore, NavigateNext, Today } from '@mui/icons-material';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';

const API = '/api';
const SC = {
  scheduled:'#2196F3', confirmed:'#9C27B0', completed:'#4CAF50',
  cancelled:'#F44336', 'no-show':'#FF9800',
  pending:'#2196F3', assigned:'#9C27B0', follow_up:'#FF9800',
  converted:'#4CAF50', closed:'#9E9E9E'
};

const HOURS = Array.from({length:13},(_,i)=>i+8); // 8am to 8pm
const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

export default function Calendar() {
  const {user} = useContext(AuthContext);
  const [appts, setAppts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [view, setView] = useState('month'); // month | week | day
  const [current, setCurrent] = useState(new Date());

  const hdr = {headers:{Authorization:'Bearer '+localStorage.getItem('token')}};

  const load = async () => {
    try {
      setLoading(true);
      const r = await axios.get(API+'/appointments', hdr);
      setAppts(r.data.appointments||[]);
    } catch(e) { setError(e.response?.data?.message||'Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(()=>{load();},[]);

  const isSameDay = (d1,d2) => {
    const a=new Date(d1),b=new Date(d2);
    return a.getFullYear()===b.getFullYear()&&a.getMonth()===b.getMonth()&&a.getDate()===b.getDate();
  };

  const getApptsByDay = (date) => appts.filter(a=>isSameDay(a.startTime,date));
  const getApptsByHour = (date,hour) => appts.filter(a=>{
    const d=new Date(a.startTime);
    return isSameDay(d,date)&&d.getHours()===hour;
  });

  const navigate = (dir) => {
    const d = new Date(current);
    if(view==='month') d.setMonth(d.getMonth()+dir);
    else if(view==='week') d.setDate(d.getDate()+7*dir);
    else d.setDate(d.getDate()+dir);
    setCurrent(d);
  };

  const getWeekDays = () => {
    const d = new Date(current);
    d.setDate(d.getDate()-d.getDay());
    return Array.from({length:7},(_,i)=>{const nd=new Date(d);nd.setDate(d.getDate()+i);return nd;});
  };

  const getMonthDays = () => {
    const year=current.getFullYear(),month=current.getMonth();
    const firstDay=new Date(year,month,1).getDay();
    const daysInMonth=new Date(year,month+1,0).getDate();
    const days=[];
    for(let i=0;i<firstDay;i++) days.push(null);
    for(let i=1;i<=daysInMonth;i++) days.push(new Date(year,month,i));
    return days;
  };

  const ApptChip = ({a}) => (
    <Box sx={{bgcolor:SC[a.appointmentStatus||a.status]||'#2196F3',color:'white',borderRadius:1,px:0.8,py:0.2,mb:0.3,fontSize:10,cursor:'pointer',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
      {new Date(a.startTime).toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit'})} {a.title}
    </Box>
  );

  if(loading) return <Box display="flex" justifyContent="center" mt={8}><CircularProgress sx={{color:'#003e6f'}}/></Box>;

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Calendar</Typography>
          <Typography variant="body2" color="text.secondary">{appts.length} total appointments</Typography>
        </Box>
        <Box display="flex" gap={1} alignItems="center">
          {['month','week','day'].map(v=>(
            <Button key={v} size="small" variant={view===v?'contained':'outlined'} onClick={()=>setView(v)}
              sx={{minWidth:70,bgcolor:view===v?'#003e6f':'transparent',color:view===v?'white':'#003e6f',borderColor:'#003e6f','&:hover':{bgcolor:view===v?'#002a4d':'#f0f4ff'}}}>
              {v.charAt(0).toUpperCase()+v.slice(1)}
            </Button>
          ))}
        </Box>
      </Box>

      {/* Navigation */}
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <Button size="small" variant="outlined" onClick={()=>setCurrent(new Date())} startIcon={<Today/>} sx={{borderColor:'#003e6f',color:'#003e6f'}}>Today</Button>
        <Button size="small" onClick={()=>navigate(-1)} sx={{minWidth:32,color:'#003e6f'}}><NavigateBefore/></Button>
        <Typography fontWeight={700} fontSize={16} sx={{minWidth:200,textAlign:'center'}}>
          {view==='month'&&MONTHS[current.getMonth()]+' '+current.getFullYear()}
          {view==='week'&&(()=>{const w=getWeekDays();return w[0].toLocaleDateString('en-IN',{day:'numeric',month:'short'})+' – '+w[6].toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'});})()}
          {view==='day'&&current.toLocaleDateString('en-IN',{weekday:'long',day:'numeric',month:'long',year:'numeric'})}
        </Typography>
        <Button size="small" onClick={()=>navigate(1)} sx={{minWidth:32,color:'#003e6f'}}><NavigateNext/></Button>
      </Box>

      {error&&<Alert severity="error" sx={{mb:2}}>{error}</Alert>}

      {/* Legend */}
      <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
        {Object.entries({Scheduled:'#2196F3',Confirmed:'#9C27B0',Completed:'#4CAF50',Cancelled:'#F44336','No-Show':'#FF9800','Converted':'#4CAF50','Closed':'#9E9E9E'}).map(([k,v])=>(
          <Chip key={k} label={k} size="small" sx={{bgcolor:v+'22',color:v,fontWeight:600,fontSize:10,height:20}}/>
        ))}
      </Box>

      {/* Month View */}
      {view==='month'&&(
        <Paper elevation={1} sx={{borderRadius:2,overflow:'hidden'}}>
          <Box display="grid" gridTemplateColumns="repeat(7,1fr)" sx={{bgcolor:'#003e6f'}}>
            {DAYS.map(d=><Box key={d} sx={{p:1,textAlign:'center',color:'white',fontSize:12,fontWeight:600}}>{d}</Box>)}
          </Box>
          <Box display="grid" gridTemplateColumns="repeat(7,1fr)" sx={{bgcolor:'white'}}>
            {getMonthDays().map((day,i)=>(
              <Box key={i} onClick={()=>{if(day){setCurrent(day);setView('day');}}}
                sx={{minHeight:100,p:0.8,border:'1px solid #f0f0f0',bgcolor:day&&isSameDay(day,new Date())?'#E8F0FE':'white',cursor:day?'pointer':'default','&:hover':{bgcolor:day?'#f0f4ff':'white'}}}>
                {day&&(
                  <>
                    <Typography fontSize={12} fontWeight={isSameDay(day,new Date())?700:400} color={isSameDay(day,new Date())?'#003e6f':'text.primary'} mb={0.5}>{day.getDate()}</Typography>
                    {getApptsByDay(day).slice(0,3).map((a,j)=><ApptChip key={j} a={a}/>)}
                    {getApptsByDay(day).length>3&&<Typography fontSize={9} color="text.secondary">+{getApptsByDay(day).length-3} more</Typography>}
                  </>
                )}
              </Box>
            ))}
          </Box>
        </Paper>
      )}

      {/* Week View */}
      {view==='week'&&(
        <Paper elevation={1} sx={{borderRadius:2,overflow:'hidden'}}>
          <Box display="grid" gridTemplateColumns="60px repeat(7,1fr)" sx={{bgcolor:'#003e6f'}}>
            <Box sx={{p:1}}/>
            {getWeekDays().map((d,i)=>(
              <Box key={i} sx={{p:1,textAlign:'center',cursor:'pointer','&:hover':{bgcolor:'rgba(255,255,255,0.1)'}}} onClick={()=>{setCurrent(d);setView('day');}}>
                <Typography fontSize={11} color="rgba(255,255,255,0.7)">{DAYS[d.getDay()]}</Typography>
                <Typography fontSize={16} fontWeight={isSameDay(d,new Date())?700:400} color={isSameDay(d,new Date())?'#FCB812':'white'}>{d.getDate()}</Typography>
              </Box>
            ))}
          </Box>
          <Box sx={{maxHeight:600,overflow:'auto'}}>
            {HOURS.map(h=>(
              <Box key={h} display="grid" gridTemplateColumns="60px repeat(7,1fr)" sx={{borderTop:'1px solid #f0f0f0',minHeight:60}}>
                <Box sx={{p:0.5,color:'#999',fontSize:11,textAlign:'right',pr:1,pt:0.5}}>{h>12?h-12+'PM':h===12?'12PM':h+'AM'}</Box>
                {getWeekDays().map((d,i)=>(
                  <Box key={i} sx={{borderLeft:'1px solid #f0f0f0',p:0.3,bgcolor:isSameDay(d,new Date())?'#E8F0FE22':'white'}}>
                    {getApptsByHour(d,h).map((a,j)=><ApptChip key={j} a={a}/>)}
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        </Paper>
      )}

      {/* Day View */}
      {view==='day'&&(
        <Paper elevation={1} sx={{borderRadius:2,overflow:'hidden'}}>
          <Box sx={{bgcolor:'#003e6f',p:1.5,textAlign:'center'}}>
            <Typography color="white" fontWeight={700}>{current.toLocaleDateString('en-IN',{weekday:'long',day:'numeric',month:'long'})}</Typography>
            <Typography color="rgba(255,255,255,0.7)" fontSize={12}>{getApptsByDay(current).length} appointments</Typography>
          </Box>
          <Box sx={{maxHeight:600,overflow:'auto'}}>
            {HOURS.map(h=>(
              <Box key={h} display="flex" sx={{borderTop:'1px solid #f0f0f0',minHeight:70}}>
                <Box sx={{width:70,p:1,color:'#999',fontSize:12,textAlign:'right',flexShrink:0}}>{h>12?h-12+':00 PM':h===12?'12:00 PM':h+':00 AM'}</Box>
                <Box sx={{flex:1,p:0.5,borderLeft:'1px solid #f0f0f0'}}>
                  {getApptsByHour(current,h).map((a,j)=>(
                    <Box key={j} sx={{bgcolor:SC[a.appointmentStatus||a.status]||'#2196F3',color:'white',borderRadius:1.5,p:1,mb:0.5}}>
                      <Typography fontSize={13} fontWeight={600}>{a.title}</Typography>
                      <Typography fontSize={11}>{a.lead?.name||a.lead?.firstName} — {a.lead?.phone}</Typography>
                      {a.location&&<Typography fontSize={11}>📍 {a.location}</Typography>}
                      <Chip label={a.appointmentStatus||a.status} size="small" sx={{mt:0.3,fontSize:9,height:16,bgcolor:'rgba(255,255,255,0.2)',color:'white'}}/>
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        </Paper>
      )}
    </Box>
  );
}
